import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { useUser } from 'builder/hooks/useUser'
import { useGetAddonPrice } from 'builder/hooks/useGetAddonPrice'
import { goToBillingPlanPage } from 'builder/utils/goToBillingPlanPage'

import Colors from 'builder/styles/colors'
import { Logo } from 'builder/components/Logo'
import Button, { ButtonTheme } from 'builder/components/Button'

import { ToggleCard } from './ToggleCard/ToggleCard'

import * as Styles from './styles'

const TRANSLATION = 'builder.auto_apply.dual_modal'
const AA_TRANSLATION = `${TRANSLATION}.cards.auto_apply`
const RD_TRANSLATION = `${TRANSLATION}.cards.resume_distribution`

interface Props {
  closeModal: () => void
}

export const SupportedUserAutoApplyResumeDistributionModal = ({ closeModal }: Props) => {
  const user = useUser()
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const [cards, setCards] = useState({ autoApply: false, resumeDistribution: true })

  useEffect(() => {
    trackInternalEvent('show_dual_modal')
  }, [])

  const { upsellAmount, recurringAmount, isLoading } = useGetAddonPrice({
    addon: '20_credits_autoapply',
    fetchPrice: true,
  })

  const getAutoApplyFooterPrice = () => {
    if (isLoading) {
      return (
        <Styles.LoaderContainer>
          <Styles.PriceLoader />
        </Styles.LoaderContainer>
      )
    }

    return i18n.t(`${AA_TRANSLATION}.footer`, { price: recurringAmount() })
  }

  const getConfirmButtonLabel = () => {
    if (!cards.autoApply) return i18n.t(`${TRANSLATION}.confirm_button`)

    if (user?.isFree || user?.isPostPremium || user?.isTopResumeUser) {
      return `${i18n.t(`${TRANSLATION}.upgrade_button`)} ${recurringAmount()}`
    }

    if (recurringAmount() === upsellAmount()) {
      return (
        <span>
          {i18n.t(`${TRANSLATION}.upgrade_button`)} {upsellAmount()}
        </span>
      )
    }

    return (
      <span>
        {i18n.t(`${TRANSLATION}.upgrade_button`)}{' '}
        <Styles.SlashedPrice>{recurringAmount()}</Styles.SlashedPrice> {upsellAmount()}
      </span>
    )
  }

  const onConfirm = () => {
    if (cards.autoApply) {
      goToBillingPlanPage({
        referrerPage: 'auto-apply',
        upgradeURL: '/app/billing/payment',
        onSuccessRedirect: '/app/auto-apply/start',
        plan: '20_credits_autoapply',
      })
    } else if (cards.resumeDistribution) {
      closeModal()
      navigate('/resume-distribution')
    }
  }

  return (
    <Styles.Modal>
      <Styles.Container>
        <Styles.HeaderMenu>
          <Logo />
          <Styles.CloseButton onClick={closeModal} />
        </Styles.HeaderMenu>
        <Styles.ModalContainer>
          <Styles.HeaderContainer>
            <Styles.Header>{i18n.t(`${TRANSLATION}.header`)}</Styles.Header>
            <Styles.SubHeader>{i18n.t(`${TRANSLATION}.sub_header`)}</Styles.SubHeader>
          </Styles.HeaderContainer>
          <Styles.CardContainer>
            <Styles.Card>
              <Styles.CardHeader $backgroundColor={Colors.Green10}>
                <Styles.CardHeaderText $textColor={Colors.Green80}>
                  {i18n.t(`${RD_TRANSLATION}.top_header`)}
                </Styles.CardHeaderText>
                <Styles.CardHeaderIcon />
              </Styles.CardHeader>
              <Styles.CardBody>
                <ToggleCard
                  paddingFeatures
                  checked={cards.resumeDistribution}
                  label={i18n.t(`${RD_TRANSLATION}.label`)}
                  header={i18n.t(`${RD_TRANSLATION}.header`)}
                  description={i18n.t(`${RD_TRANSLATION}.sub_header`)}
                  featureTitle={i18n.t(`${RD_TRANSLATION}.features_title`)}
                  featureList={[
                    i18n.t(`${RD_TRANSLATION}.features.1`),
                    i18n.t(`${RD_TRANSLATION}.features.2`),
                    i18n.t(`${RD_TRANSLATION}.features.3`),
                  ]}
                  footerNote={i18n.t(`${RD_TRANSLATION}.footer`)}
                  onToggle={value => setCards(props => ({ ...props, resumeDistribution: value }))}
                />
              </Styles.CardBody>
            </Styles.Card>
            <Styles.Card>
              <Styles.CardHeader $backgroundColor={Colors.Indigo10}>
                <Styles.CardHeaderText $textColor={Colors.Indigo80}>
                  {i18n.t(`${AA_TRANSLATION}.top_header`)}
                </Styles.CardHeaderText>
                <Styles.CardHeaderIcon $isResume />
              </Styles.CardHeader>
              <Styles.CardBody>
                <ToggleCard
                  checked={cards.autoApply}
                  header={i18n.t(`${AA_TRANSLATION}.header`)}
                  description={i18n.t(`${AA_TRANSLATION}.sub_header`)}
                  featureTitle={i18n.t(`${AA_TRANSLATION}.features_title`)}
                  featureList={[
                    i18n.t(`${AA_TRANSLATION}.features.1`),
                    i18n.t(`${AA_TRANSLATION}.features.2`),
                    i18n.t(`${AA_TRANSLATION}.features.3`),
                  ]}
                  footerNote={getAutoApplyFooterPrice()}
                  onToggle={value => setCards(props => ({ ...props, autoApply: value }))}
                />
              </Styles.CardBody>
            </Styles.Card>
          </Styles.CardContainer>
          <Styles.ActionContainer>
            <Button onClick={closeModal} theme={ButtonTheme.ghost}>
              {i18n.t(`${TRANSLATION}.skip_button`)}
            </Button>
            <Button onClick={onConfirm}>{getConfirmButtonLabel()}</Button>
          </Styles.ActionContainer>
        </Styles.ModalContainer>
      </Styles.Container>
    </Styles.Modal>
  )
}
